import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Copy, Trash2, Plus, RefreshCw, ExternalLink } from 'lucide-react';

/**
 * Admin → Private tab.
 * Generate tracking links + review every visit that was captured.
 *
 * Two panels stacked:
 *  1. Links: label + slug + hits + copy public URL + delete
 *  2. Visits: full forensic record (IP, ISP, geo, headers, device profile)
 */

const API = process.env.REACT_APP_BACKEND_URL;

// Fields we surface prominently at the top of a visit row.
const PRIMARY_FIELDS = [
  ['ip', 'IP'],
  ['isp', 'ISP'],
  ['org', 'Org'],
  ['asn', 'ASN'],
  ['country', 'Country'],
  ['region', 'Region'],
  ['city', 'City'],
  ['postal', 'Postal'],
  ['user_agent', 'User-Agent'],
  ['accept_language', 'Accept-Language'],
  ['referrer', 'Referrer'],
];

const HIDDEN_KEYS = new Set([
  'id', 'slug', 'label', 'created_at',
  ...PRIMARY_FIELDS.map(([k]) => k),
]);

function copyToClipboard(text) {
  try {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  } catch {
    toast.error('Copy failed');
  }
}

function fmt(v) {
  if (v === null || v === undefined || v === '') return '-';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  return String(v);
}

const PrivateTab = ({ token }) => {
  const [links, setLinks] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  const authFetch = useCallback(
    (path, init = {}) =>
      fetch(`${API}${path}`, {
        ...init,
        headers: {
          ...(init.headers || {}),
          Authorization: `Bearer ${token}`,
        },
      }),
    [token],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [lr, vr] = await Promise.all([
        authFetch('/api/admin/private/honeypot-links'),
        authFetch('/api/admin/private/honeypot-visits'),
      ]);
      if (lr.ok) setLinks((await lr.json()).links || []);
      if (vr.ok) setVisits((await vr.json()).visits || []);
    } catch {
      toast.error('Could not load honeypot data');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!label.trim()) {
      toast.error('Label is required');
      return;
    }
    setCreating(true);
    try {
      const res = await authFetch('/api/admin/private/honeypot-links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: label.trim() }),
      });
      if (!res.ok) throw new Error('failed');
      const link = await res.json();
      setLabel('');
      setLinks((prev) => [link, ...prev]);
      const fullUrl = `${window.location.origin}/private/${link.slug}`;
      copyToClipboard(fullUrl);
      toast.success('Link generated - URL copied to clipboard');
    } catch {
      toast.error('Could not create link');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteLink = async (slug) => {
    if (!window.confirm('Delete this tracking link? Existing captured visits will remain.')) return;
    try {
      const res = await authFetch(`/api/admin/private/honeypot-links/${slug}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error();
      setLinks((prev) => prev.filter((l) => l.slug !== slug));
      toast.success('Link deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleDeleteVisit = async (id) => {
    if (!window.confirm('Delete this captured visit permanently?')) return;
    try {
      const res = await authFetch(`/api/admin/private/honeypot-visits/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error();
      setVisits((prev) => prev.filter((v) => v.id !== id));
      toast.success('Visit deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8" data-testid="private-tab">
      {/* Warning banner */}
      <div className="bg-red-950/40 border border-red-900/50 rounded-lg p-4">
        <p className="text-red-300 text-xs uppercase tracking-widest font-bold mb-1">Confidential</p>
        <p className="text-red-100 text-sm leading-relaxed">
          These tracking links silently record the full network + device
          fingerprint of anyone who visits them and email you the record
          immediately. Only share these URLs privately with suspected
          individuals. Do NOT link them from any public page.
        </p>
      </div>

      {/* Generate link */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-5">
        <h3 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
          <Plus className="h-4 w-4" /> Generate Tracking Link
        </h3>
        <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder='Label - e.g. "Suspect: J. Smith 21/8"'
            className="flex-1 bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none"
            data-testid="private-link-label-input"
            maxLength={160}
          />
          <button
            type="submit"
            disabled={creating || !label.trim()}
            className="bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white font-bold px-5 py-2 rounded text-sm flex items-center justify-center gap-2"
            data-testid="private-link-create-btn"
          >
            <Plus className="h-4 w-4" /> Generate
          </button>
        </form>
        <p className="text-zinc-500 text-xs mt-2">
          Copies the full URL to your clipboard. Share it privately.
        </p>
      </div>

      {/* Links list */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-bold text-lg">Tracking Links ({links.length})</h3>
          <button
            onClick={refresh}
            className="text-zinc-400 hover:text-white flex items-center gap-1 text-xs"
            data-testid="private-refresh-btn"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
        {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
        {!loading && links.length === 0 && (
          <p className="text-zinc-500 text-sm">No links yet. Generate your first tracking link above.</p>
        )}
        <div className="space-y-2">
          {links.map((l) => {
            const fullUrl = `${window.location.origin}/private/${l.slug}`;
            return (
              <div
                key={l.slug}
                className="bg-zinc-900/40 border border-zinc-800 rounded p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                data-testid={`private-link-row-${l.slug}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{l.label}</p>
                  <p className="text-zinc-500 text-xs font-mono truncate">{fullUrl}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className="text-xs font-bold px-2 py-1 rounded bg-red-950/50 text-red-300 border border-red-900/40"
                    data-testid={`private-link-hits-${l.slug}`}
                  >
                    {l.hits || 0} hit{(l.hits || 0) === 1 ? '' : 's'}
                  </span>
                  <button
                    onClick={() => copyToClipboard(fullUrl)}
                    title="Copy URL"
                    className="text-zinc-400 hover:text-white p-1"
                    data-testid={`private-link-copy-${l.slug}`}
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <a
                    href={fullUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open in new tab (will register a hit from you!)"
                    className="text-zinc-400 hover:text-white p-1"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <button
                    onClick={() => handleDeleteLink(l.slug)}
                    title="Delete link"
                    className="text-zinc-400 hover:text-red-400 p-1"
                    data-testid={`private-link-delete-${l.slug}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Visits list */}
      <div>
        <h3 className="text-white font-bold text-lg mb-3">Captured Visits ({visits.length})</h3>
        {!loading && visits.length === 0 && (
          <p className="text-zinc-500 text-sm">No captures yet.</p>
        )}
        <div className="space-y-2">
          {visits.map((v) => {
            const isExpanded = expanded.has(v.id);
            const extraKeys = Object.keys(v).filter((k) => !HIDDEN_KEYS.has(k));
            return (
              <div
                key={v.id}
                className="bg-zinc-900/40 border border-zinc-800 rounded p-4"
                data-testid={`private-visit-row-${v.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-mono font-bold text-base break-all" data-testid={`private-visit-ip-${v.id}`}>
                      {v.ip || '-'}
                    </p>
                    <p className="text-zinc-400 text-xs mt-0.5">
                      {v.city && `${v.city}, `}{v.region && `${v.region}, `}{v.country || 'Unknown location'} · {v.isp || 'Unknown ISP'}
                    </p>
                    <p className="text-zinc-500 text-[11px] mt-1 font-mono">
                      {v.created_at?.replace('T', ' ').split('.')[0]} UTC
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-red-400 text-xs font-bold uppercase tracking-wide">
                      {v.label || v.slug || '(direct)'}
                    </p>
                    <button
                      onClick={() => handleDeleteVisit(v.id)}
                      title="Delete this record"
                      className="text-zinc-500 hover:text-red-400 mt-1"
                      data-testid={`private-visit-delete-${v.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                  {PRIMARY_FIELDS.map(([k, lbl]) => (
                    <div key={k} className="min-w-0">
                      <span className="text-zinc-500">{lbl}: </span>
                      <span className="text-zinc-200 font-mono break-all">{fmt(v[k])}</span>
                    </div>
                  ))}
                </div>

                {extraKeys.length > 0 && (
                  <button
                    onClick={() => toggleExpand(v.id)}
                    className="text-red-400 hover:text-red-300 text-xs font-bold mt-3"
                    data-testid={`private-visit-toggle-${v.id}`}
                  >
                    {isExpanded ? '− Hide device profile' : `+ Show device profile (${extraKeys.length} fields)`}
                  </button>
                )}
                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-zinc-800 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    {extraKeys.map((k) => (
                      <div key={k} className="min-w-0">
                        <span className="text-zinc-500">{k.replace('client_', '').replace(/_/g, ' ')}: </span>
                        <span className="text-zinc-200 font-mono break-all">{fmt(v[k])}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PrivateTab;
